import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FolderIcon from "@mui/icons-material/Folder";

export default function CopyFileDialog({ 
  open, 
  onClose, 
  originalFile, 
  onCopy 
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('filesPage');
  const [newFileName, setNewFileName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Dialog açıldığında default isim oluştur
  useEffect(() => {
    if (open && originalFile) {
      setNewFileName(`${originalFile.name} - ${t('fileCopySuffix')}`);
      setError("");
    }
  }, [open, originalFile, t]);

  const handleSubmit = async () => {
    setError("");

    if (!newFileName || !newFileName.trim()) {
      setError(t('pleaseEnterFileName'));
      return;
    }

    if (newFileName.trim().length < 2) {
      setError(t('fileNameMinLength'));
      return;
    }

    try {
      setLoading(true);
      await onCopy(newFileName.trim());
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || t('errorCopyingFile'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewFileName("");
    setError("");
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          border: `1px solid ${colors.grey[800]}30`,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.blueAccent[700]}60 0%, ${colors.primary[500]} 100%)`,
          borderBottom: `2px solid ${colors.blueAccent[500]}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ContentCopyIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.grey[100],
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {t('copyFileDialogTitle')}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: colors.grey[400],
            "&:hover": {
              color: colors.grey[100],
              backgroundColor: `${colors.redAccent[800]}30`,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Original File Info */}
        {originalFile && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: "12px",
              background: `${colors.blueAccent[800]}20`,
              border: `1px solid ${colors.blueAccent[500]}40`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.grey[400], mb: 1, fontSize: "0.75rem" }}
            >
              {t('copyingFrom')}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FolderIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
              <Typography
                variant="body1"
                sx={{ color: colors.grey[100], fontWeight: 600 }}
              >
                {originalFile.name}
              </Typography>
            </Box>
            {originalFile.Forms && originalFile.Forms.length > 0 && (
              <Typography
                variant="caption"
                sx={{ color: colors.grey[400], mt: 0.5, display: "block" }}
              >
                {t('formsWillBeCopied', { count: originalFile.Forms.length })}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 3, borderColor: `${colors.grey[800]}30` }} />

        {/* New File Name Input */}
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 1.5, color: colors.grey[300], fontWeight: 600 }}
          >
            {t('newFileName')}
          </Typography>
          <TextField
            fullWidth
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            placeholder={t('enterNewFileName')}
            autoFocus
            error={!!error}
            helperText={error}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: colors.primary[600],
                "& fieldset": {
                  borderColor: `${colors.grey[800]}40`,
                },
                "&:hover fieldset": {
                  borderColor: colors.blueAccent[500],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.greenAccent[500],
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: colors.grey[100],
                fontSize: "1rem",
              },
              "& .MuiFormHelperText-root": {
                color: colors.redAccent[400],
              },
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "12px",
            background: `${colors.greenAccent[800]}15`,
            border: `1px solid ${colors.greenAccent[500]}30`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: colors.grey[400], fontSize: "0.8rem" }}
            dangerouslySetInnerHTML={{ __html: t('copyFileNote') }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          gap: 1,
          borderTop: `1px solid ${colors.grey[800]}30`,
          pt: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: colors.grey[300],
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: `${colors.grey[800]}30`,
              color: colors.grey[100],
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
            color: "white",
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
            fontWeight: 600,
            boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
            },
            "&:disabled": {
              background: colors.grey[600],
              color: colors.grey[400],
            },
          }}
        >
          {loading ? t('copying') : t('createCopy')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}








